import { FC, useEffect, useRef, useState } from 'react'
import { KTSVG } from '../../../_metronic/helpers'
import { useDataTableStore } from '../../../stores/DataTableStore'
import Swal from 'sweetalert2'
import * as Yup from 'yup'
import useAccessToken from '../../../hooks/AccessToken'
import { useSettingsStore } from '../../../stores/SettingsStore'
import { useIntl } from 'react-intl'
import { addTemplateSettingMeeting } from '../../../api/SettingCRUD'
import useHandleCloseModal from '../../../hooks/HandleCloseModal'
import { useFormik } from 'formik'
import clsx from 'clsx'
import { handleCaps } from '../../../functions/general'
import { useMeetingStore } from '../../../stores/MeetingsStore'
import VirtualizedSelect, { VirtualizedSelectOption } from '../../Select/SelectVirtualized'
import { useMerchantStore } from '../../../stores/MerchantStore'
import { SettingMultiMerchants } from '../../../interfaces/Settings'

interface Props {
}

const AddTemplateMeeting: FC<Props> = ({ }) => {
  const token = useAccessToken()
  const { multiMeetings, setMultiMeetings } = useMeetingStore()
  const { multiMerchants } = useMerchantStore()
  const { settings } = useSettingsStore()

  const closeModalRef = useRef<HTMLButtonElement>(null)
  const modalRef = useRef<HTMLDivElement | null>(null)

  const [merchantOptions, setMerchantOptions] = useState<VirtualizedSelectOption[]>([])
  const [modalBtnLoading, setModalBtnLoading] = useState(false)

  const intl = useIntl()

  const allOptionMerchant = [{ label: intl.formatMessage({ id: "FORM.LABEL.ALL" }, { title: settings.merchant_title }), value: 'all' }]

  const formik = useFormik({
    initialValues: {
      name: '',
      merchantTemplateIds: allOptionMerchant,
    },
    validationSchema: Yup.object().shape({
      name: Yup.string().required(intl.formatMessage({ id: 'FORM.VALIDATION.FIELD_REQUIRED' },{ title: intl.formatMessage({ id: 'FORM.LABEL.NAME' }) })).max(30, intl.formatMessage({ id: "FORM.VALIDATION.MAXIMAL" }, { value: '30' })),
      merchantTemplateIds: Yup.array().required(intl.formatMessage({ id: 'FORM.VALIDATION.FIELD_REQUIRED' },{ title: settings.merchant_title })),
    }),
    onSubmit: async (values,{ setSubmitting }) => {
      setModalBtnLoading(true)
      setSubmitting(true)

      const listMerchantIds = values.merchantTemplateIds.map((item: any) => item.value)

      try {
        const response = await addTemplateSettingMeeting(token, values.name, listMerchantIds, {}, true, 'CREATED', 'THIS_MONTH')
        if (response.data.success) {
          setMultiMeetings([
            ...multiMeetings,
            { ...response.data.data, label: response.data.data.name, value: response.data.data.id },
          ])
          closeModalRef.current?.click()

          Swal.fire({
            icon: 'success',
            heightAuto: false,
            title: intl.formatMessage({ id: 'FORM.VALIDATION.SAVE_SUCCESS' }),
            timer: 2000,
            timerProgressBar: true,
          })

        }
      } catch (error: any) {
        Swal.fire({
          icon: 'error',
          title: error.response.data.message,
          heightAuto: false,
          confirmButtonText: 'Ok',
        })
      } finally {
        setModalBtnLoading(false)
        setSubmitting(false)
      }
    },
  })

  useEffect(() => {
    let data = multiMerchants.map((merchant: SettingMultiMerchants) => {
      return {
        value: merchant.id.toString(),
        label: merchant.name,
      }
    })

    setMerchantOptions([...allOptionMerchant, ...data])
  }, [multiMerchants]);

  useEffect(() => {
    formik.resetForm()
  }, [])

  const { handleCloseModal } = useHandleCloseModal(modalRef, closeModalRef, formik);

  return (
    <div className="modal fade" tabIndex={-1} id="add-database-meeting-modal" ref={modalRef}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{intl.formatMessage({ id: 'FORM.ACTION.ADD_TEMPLATE' }, { title: settings.meeting_title })}</h5>
            <div className="btn btn-icon btn-sm btn-active-light-primary ms-2" onClick={handleCloseModal}>
              <KTSVG path="/media/icons/duotune/arrows/arr061.svg" className="svg-icon svg-icon-2x" />
            </div>
          </div>
          <form onSubmit={formik.handleSubmit} noValidate>
            <div className="modal-body">
              <label className='form-label fs-6 fw-bolder text-dark required'>{intl.formatMessage({ id: "FORM.LABEL.NAME" })}</label>
              <div className="input-group mb-3">
                <input
                  placeholder={intl.formatMessage({ id: 'FORM.LABEL.NAME' })}
                  {...formik.getFieldProps('name')}
                  className={clsx('form-control form-control-lg', {
                    'is-invalid': formik.touched.name && formik.errors.name,
                  })}
                  type='text'
                  name='name'
                  autoComplete='off'
                  onInput={(e) => handleCaps(e)}
                />
              </div>
              {formik.touched.name && formik.errors.name && (
                <div className='fv-plugins-message-container text-danger'>
                  <span role='alert' className="text-danger">{formik.errors.name}</span>
                </div>
              )}

              <span className='fs-6 fw-bolder text-dark required'>{intl.formatMessage({ id: "FORM.LABEL.SETTING_TEMPLATE" }, { title: settings.merchant_title })}</span><br />
              <span className="text-muted fs-6">{intl.formatMessage({ id: "FORM.LABEL.DESC_SETTING_MEETING" }, { title: settings.merchant_title })}</span>
              <div className="input-group mb-3">
                <VirtualizedSelect
                  options={merchantOptions}
                  placeholder={`${intl.formatMessage({ id: 'FORM.ACTION.CHOOSE' })} ${intl.formatMessage({ id: 'FILTER.LABEL.DATABASE' })} ${settings.merchant_title}`}
                  onChange={(selectedOptions: VirtualizedSelectOption[]) => {
                    if (selectedOptions.length === 0) {
                      formik.setFieldValue('merchantTemplateIds', allOptionMerchant)
                    } else {
                      var endIndex = selectedOptions.length - 1
                      if (selectedOptions[endIndex].value === 'all') {
                        formik.setFieldValue('merchantTemplateIds', allOptionMerchant)
                      } else {
                        formik.setFieldValue('merchantTemplateIds', selectedOptions.filter(item => item.value !== 'all'))
                      }
                    }
                  }}
                  className='w-100'
                  value={formik.values.merchantTemplateIds}
                  isMulti
                  isSort={false}
                  closeMenuOnSelect={false}
                />
              </div>
              {formik.touched.merchantTemplateIds && formik.errors.merchantTemplateIds && (
                <div className='fv-plugins-message-container text-danger'>
                  <span role='alert' className="text-danger">{formik.errors.merchantTemplateIds}</span>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-light d-none"
                data-bs-dismiss="modal"
                ref={closeModalRef}
              >
                {intl.formatMessage({ id: "FORM.ACTION.CANCEL" })}
              </button>
              <button
                type="button"
                className="btn btn-light"
                onClick={handleCloseModal}
              >
                {intl.formatMessage({ id: "FORM.ACTION.CANCEL" })}
              </button>
              <button type="submit" className={`btn btn-primary me-10 ${modalBtnLoading ? 'disabled' : ''}`} data-kt-indicator={modalBtnLoading ? 'on' : 'off'}>
                <span className="indicator-label">
                  {intl.formatMessage({ id: "FORM.ACTION.ADD" })}
                </span>
                <span className="indicator-progress">
                  Loading... <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                </span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export { AddTemplateMeeting }
